import { Flex, Button, Spinner, Text, Separator } from "@radix-ui/themes";
import Checklist from "@/components/shared/inputs/checklist";
import { useArticleCategorySelector } from "@/v2/shared/hooks/useArticleCategorySelector";
import { useCallback } from "react";
import styles from "./ArticleCategoryContent.module.scss";

export interface ArticleCategoryContentProps
  extends ReturnType<typeof useArticleCategorySelector> {
  source: string;
  onSave: () => void;
  onClose: () => void;
  loadingText?: string;
  loadingSubtext?: string;
  showButtons?: boolean;
  saveButtonText?: string;
  cancelButtonText?: string;
}

export function ArticleCategoryContent({
  source,
  onSave,
  onClose,
  loadingText = "Loading...",
  loadingSubtext = "Please wait while we fetch your configuration",
  showButtons = true,
  saveButtonText = "Save Configuration",
  cancelButtonText = "Cancel",
  ...props
}: ArticleCategoryContentProps) {
  const {
    tenantBrands,
    brandCategories,
    updateBrandSelectedCategories,
    isSaving,
    isLoading,
    handleSaveConfiguration,
  } = props;

  const handleSave = useCallback(async () => {
    await handleSaveConfiguration();
    onSave();
  }, [handleSaveConfiguration, onSave]);

  const createBrandSelectionHandler = useCallback(
    (brandSubdomain: string) => (selectedIds: string[]) =>
      updateBrandSelectedCategories(brandSubdomain, selectedIds),
    [updateBrandSelectedCategories]
  );

  if (isLoading) {
    return (
      <Flex direction="column" gap="4" align="center" py="6">
        <Spinner size="3" />
        <Flex direction="column" gap="2" align="center">
          <Text size="2" color="gray">
            {loadingText}
          </Text>
          <Text size="1" color="gray">
            {loadingSubtext}
          </Text>
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap="4">
      <div className={styles.scrollContainer}>
        <Flex direction="column" gap="6" pb="5">
          {tenantBrands?.map((brand, index) => {
            const brandData = brandCategories[brand.subdomain];
            if (!brandData) return <></>;

            return (
              <Flex key={brand.subdomain} direction="column" gap="4">
                {index > 0 && (
                  <Separator
                    size="2"
                    style={{
                      width: "100%",
                      alignSelf: "stretch",
                      borderColor: "var(--accent-a5)",
                      borderBottomWidth: 2,
                      margin: "24px 0 12px 0",
                    }}
                    className="article-category-brand-separator"
                  />
                )}
                <Checklist
                  items={brandData.available}
                  selectedIds={brandData.selected}
                  onSelectionChange={createBrandSelectionHandler(
                    brand.subdomain
                  )}
                  title={`Brand: ${brand.name}`}
                />
              </Flex>
            );
          })}
        </Flex>
      </div>
      {showButtons && (
        <Flex gap="3" mt="4" justify="end">
          <Button variant="soft" color="gray" highContrast onClick={onClose}>
            {cancelButtonText}
          </Button>
          <Button
            color="gray"
            variant="solid"
            highContrast
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <Spinner size="1" />
                Saving...
              </>
            ) : (
              saveButtonText
            )}
          </Button>
        </Flex>
      )}
    </Flex>
  );
}
